import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ArrowDownTrayIcon,
  ArrowUpIcon,
  ArrowDownIcon
} from '@heroicons/react/24/outline';
import realtorApiService, { Office } from '../services/realtorApiService';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';

interface BrokerOfRecord {
  name: string;
  officeName: string;
  officeKey: string;
  memberKey: string;
  phone: string;
  province: string;
}

interface OfficeWithBroker extends Office {
  brokerOfRecord?: BrokerOfRecord;
  hasBroker: boolean;
}

const BrokeragesList: React.FC = () => {
  const [offices, setOffices] = useState<OfficeWithBroker[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedOffices, setExpandedOffices] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [filterProvince, setFilterProvince] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterNoBroker, setFilterNoBroker] = useState<boolean>(false);
  const [sortField, setSortField] = useState<'OfficeName' | 'OfficeStateOrProvince'>('OfficeName');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;

  // Test function to check Ontario count
  const testOntarioCount = async () => {
    try {
      const functions = getFunctions(getApp());
      const testFn = httpsCallable(functions, 'testOntarioOffices');
      const result = await testFn();
      const data = result.data as any;
      console.log('Ontario Offices Test:', data);
      alert(`Total Active Offices in Ontario: ${data.totalCount || 'unknown'}\nFirst page: ${data.firstPageCount}\nHas next page: ${data.hasNextLink ? 'Yes' : 'No'}`);
    } catch (error: any) {
      console.error('Test error:', error);
      alert(`Error: ${error.message}`);
    }
  };

  // Diagnostic function to check filters
  const diagnoseFilters = async () => {
    try {
      const functions = getFunctions(getApp());
      const diagnoseFn = httpsCallable(functions, 'diagnoseOfficeFilters');
      const result = await diagnoseFn({ province: 'Ontario' });
      const data = result.data as any;
      console.log('Filter Diagnosis:', data);
      
      let message = 'Filter Diagnosis Results:\n\n';
      data.tests.forEach((test: any) => {
        message += `${test.name}: ${test.count}\n`;
      });
      message += `\nStatus Breakdown (from sample):\n`;
      Object.entries(data.statusBreakdown || {}).forEach(([status, count]) => {
        message += `  ${status}: ${count}\n`;
      });
      
      alert(message);
    } catch (error: any) {
      console.error('Diagnosis error:', error);
      alert(`Error: ${error.message}`);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch offices from Ontario and Alberta, only active
      const officeFilter = "(OfficeStateOrProvince eq 'Ontario' or OfficeStateOrProvince eq 'Alberta') and OfficeStatus eq 'Active'";
      const allOffices = await realtorApiService.getAllOffices({
        $filter: officeFilter,
        $orderby: 'OfficeName asc'
      });

      // Fetch all Broker of Record members from Ontario and Alberta, only active
      const memberFilter = "(MemberStateOrProvince eq 'Ontario' or MemberStateOrProvince eq 'Alberta') and MemberStatus eq 'Active' and JobTitle eq 'Broker of Record'";
      const allMembers = await realtorApiService.getAllMembers({
        $filter: memberFilter
      });

      // Filter out members with no office
      const membersWithOffice = allMembers.filter(m => m.OfficeKey);

      // Create a map of OfficeKey -> Broker of Record
      // Note: If multiple brokers exist for same office, first one wins (shouldn't happen per requirements)
      const brokerMap = new Map<string, BrokerOfRecord>();
      membersWithOffice.forEach(member => {
        if (member.OfficeKey && !brokerMap.has(member.OfficeKey)) {
          brokerMap.set(member.OfficeKey, {
            name: `${member.MemberFirstName || ''} ${member.MemberLastName || ''}`.trim(),
            officeName: '', // Will be filled from office data
            officeKey: member.OfficeKey,
            memberKey: member.MemberKey || '',
            phone: member.MemberOfficePhone || '',
            province: member.MemberStateOrProvince || ''
          });
        }
      });

      // Combine offices with their brokers
      const officesWithBrokers: OfficeWithBroker[] = allOffices
        .filter(office => office.OfficeKey) // Only offices with keys
        .map(office => {
          const broker = brokerMap.get(office.OfficeKey || '');
          return {
            ...office,
            brokerOfRecord: broker,
            hasBroker: !!broker
          };
        })
        .map(office => {
          // Update broker's office name
          if (office.brokerOfRecord) {
            office.brokerOfRecord.officeName = office.OfficeName || '';
          }
          return office;
        });

      setOffices(officesWithBrokers);
    } catch (err: any) {
      console.error('Error loading data:', err);
      setError(err.message || 'Failed to load data from DDF API');
    } finally {
      setLoading(false);
    }
  };

  // Filter and search
  const filteredOffices = useMemo(() => {
    let filtered = [...offices];

    // Search by office name or city
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(office => 
        (office.OfficeName?.toLowerCase().includes(searchLower)) ||
        (office.OfficeCity?.toLowerCase().includes(searchLower))
      );
    }

    // Filter by province
    if (filterProvince !== 'all') {
      filtered = filtered.filter(office => office.OfficeStateOrProvince === filterProvince);
    }

    // Filter by status
    if (filterStatus !== 'all') {
      filtered = filtered.filter(office => office.OfficeStatus === filterStatus);
    }

    // Filter by no broker
    if (filterNoBroker) {
      filtered = filtered.filter(office => !office.hasBroker);
    }

    return filtered;
  }, [offices, searchTerm, filterProvince, filterStatus, filterNoBroker]);

  // Sort
  const sortedOffices = useMemo(() => {
    const sorted = [...filteredOffices];
    sorted.sort((a, b) => {
      let aValue: string | null = null;
      let bValue: string | null = null;

      if (sortField === 'OfficeName') {
        aValue = a.OfficeName || '';
        bValue = b.OfficeName || '';
      } else if (sortField === 'OfficeStateOrProvince') {
        aValue = a.OfficeStateOrProvince || '';
        bValue = b.OfficeStateOrProvince || '';
      }

      if (aValue === null && bValue === null) return 0;
      if (aValue === null) return 1;
      if (bValue === null) return -1;

      const comparison = aValue.localeCompare(bValue);
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [filteredOffices, sortField, sortDirection]);

  // Pagination
  const totalPages = Math.ceil(sortedOffices.length / itemsPerPage);
  const paginatedOffices = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return sortedOffices.slice(start, start + itemsPerPage);
  }, [sortedOffices, currentPage]);

  const toggleExpand = (officeKey: string | null) => {
    if (!officeKey) return;
    setExpandedOffices(prev => {
      const newSet = new Set(prev);
      if (newSet.has(officeKey)) {
        newSet.delete(officeKey);
      } else {
        newSet.add(officeKey);
      }
      return newSet;
    });
  };

  const handleSort = (field: 'OfficeName' | 'OfficeStateOrProvince') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const exportToCSV = () => {
    const headers = [
      'Office Name',
      'Address',
      'City',
      'Province',
      'Postal Code',
      'Phone',
      'Office Key',
      'Status',
      'Has Broker of Record',
      'Broker Name',
      'Broker Member Key',
      'Broker Phone',
      'Broker Province'
    ];

    const rows = sortedOffices.map(office => {
      const broker = office.brokerOfRecord;
      return [
        office.OfficeName || '',
        `${office.OfficeAddress1 || ''} ${office.OfficeAddress2 || ''}`.trim(),
        office.OfficeCity || '',
        office.OfficeStateOrProvince || '',
        office.OfficePostalCode || '',
        office.OfficePhone || '',
        office.OfficeKey || '',
        office.OfficeStatus || '',
        office.hasBroker ? 'Yes' : 'No',
        broker?.name || '',
        broker?.memberKey || '',
        broker?.phone || '',
        broker?.province || ''
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell.replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `brokerages_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getSortIcon = (field: 'OfficeName' | 'OfficeStateOrProvince') => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? (
      <ArrowUpIcon className="h-4 w-4 inline ml-1" />
    ) : (
      <ArrowDownIcon className="h-4 w-4 inline ml-1" />
    );
  };

  return (
    <>
      <Helmet>
        <title>Brokerages List | BizBuyMax.com</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Brokerages & Offices</h1>
                <p className="text-gray-600 mt-1">
                  Ontario & Alberta - {sortedOffices.length} offices
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <button
                  onClick={testOntarioCount}
                  className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md font-medium transition-colors"
                >
                  <span>Test Ontario Count</span>
                </button>
                <button
                  onClick={diagnoseFilters}
                  className="flex items-center space-x-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md font-medium transition-colors"
                >
                  <span>Diagnose Filters</span>
                </button>
                <button
                  onClick={exportToCSV}
                  className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium transition-colors"
                >
                  <ArrowDownTrayIcon className="h-5 w-5" />
                  <span>Export CSV</span>
                </button>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="relative">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search office name or city..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <select
                value={filterProvince}
                onChange={(e) => {
                  setFilterProvince(e.target.value);
                  setCurrentPage(1);
                }}
                className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Provinces</option>
                <option value="Ontario">Ontario</option>
                <option value="Alberta">Alberta</option>
              </select>
              <select
                value={filterStatus}
                onChange={(e) => {
                  setFilterStatus(e.target.value);
                  setCurrentPage(1);
                }}
                className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Status</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filterNoBroker}
                  onChange={(e) => {
                    setFilterNoBroker(e.target.checked);
                    setCurrentPage(1);
                  }}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">No Broker of Record</span>
              </label>
            </div>
          </div>

          {/* Error State */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-red-800">{error}</p>
              <button
                onClick={loadData}
                className="mt-2 text-red-600 hover:text-red-800 underline"
              >
                Retry
              </button>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">Loading data from DDF API...</p>
            </div>
          )}

          {/* Table */}
          {!loading && !error && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('OfficeName')}
                      >
                        Office Name {getSortIcon('OfficeName')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Address
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Phone
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Key/ID
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('OfficeStateOrProvince')}
                      >
                        Province {getSortIcon('OfficeStateOrProvince')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedOffices.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                          No offices found
                        </td>
                      </tr>
                    ) : (
                      paginatedOffices.map((office) => {
                        const isExpanded = expandedOffices.has(office.OfficeKey || '');
                        const hasBroker = office.hasBroker;
                        const broker = office.brokerOfRecord;

                        return (
                          <React.Fragment key={office.OfficeKey}>
                            <tr className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                {hasBroker && (
                                  <button
                                    onClick={() => toggleExpand(office.OfficeKey)}
                                    className="text-gray-400 hover:text-gray-600"
                                  >
                                    {isExpanded ? (
                                      <ChevronDownIcon className="h-5 w-5" />
                                    ) : (
                                      <ChevronRightIcon className="h-5 w-5" />
                                    )}
                                  </button>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">
                                  {office.OfficeName || 'N/A'}
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="text-sm text-gray-900">
                                  {office.OfficeAddress1 || ''}
                                  {office.OfficeAddress2 && `, ${office.OfficeAddress2}`}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {office.OfficeCity || ''} {office.OfficePostalCode || ''}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {office.OfficePhone || 'N/A'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {office.OfficeKey || 'N/A'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {office.OfficeStateOrProvince || 'N/A'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                  office.OfficeStatus === 'Active' 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {office.OfficeStatus || 'N/A'}
                                </span>
                              </td>
                            </tr>
                            {isExpanded && hasBroker && broker && (
                              <tr className="bg-blue-50">
                                <td colSpan={7} className="px-6 py-4">
                                  <div className="ml-8">
                                    <h4 className="text-sm font-semibold text-gray-900 mb-2">
                                      Broker of Record
                                    </h4>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                      <div>
                                        <span className="text-gray-500">Name:</span>
                                        <div className="font-medium text-gray-900">{broker.name}</div>
                                      </div>
                                      <div>
                                        <span className="text-gray-500">Member Key:</span>
                                        <div className="font-medium text-gray-900">{broker.memberKey}</div>
                                      </div>
                                      <div>
                                        <span className="text-gray-500">Phone:</span>
                                        <div className="font-medium text-gray-900">{broker.phone || 'N/A'}</div>
                                      </div>
                                      <div>
                                        <span className="text-gray-500">Province:</span>
                                        <div className="font-medium text-gray-900">{broker.province}</div>
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                      Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, sortedOffices.length)} of {sortedOffices.length} offices
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <span className="px-3 py-2 text-sm text-gray-700">
                        Page {currentPage} of {totalPages}
                      </span>
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default BrokeragesList;
